import { v4 } from 'uuid';

import { blankComponenetDimensionOptionsCfn } from './functions';
import { ComponentOptions } from '../types';
import { RecordData } from '../../helpers-classes';
import { Rules } from '../../blueprint-classes';
import { Size } from '../../types';
import { ComponentDataIn, ComponentDimensionOptions, ComponentDynamicSvg, DefaultPlacement, ImageCorrection, SkuOverheadMode } from './types';
import { ComponentItemData } from '../data/component-item-data.class';

export const componentData = (options: ComponentOptions<ComponentDataIn>): ComponentDataIn => {
	// const { noteMessage = '', artifactId = null } = params || {};
	const rd = new RecordData<ComponentDataIn>(options.dataIn);
	// Look up hsProductId from asset data if not in saved data (for override items)
	const sku = options.dataIn?.sku;
	const assetData = sku ? ComponentItemData.getDataBySku(sku) : null;

	const record: ComponentDataIn = {
		id: rd.useNewOrPartial<string>('id', v4()),
		vendorId: rd.useNewOrDefault<string>('vendorId', null),
		sku: rd.useNewOrDefault<string>('sku', null),
		label: rd.useNewOrDefault<string>('label', null),
		displayName: rd.useNewOrDefault<string>('displayName', null),
		customLabel: rd.useNewOrDefault<string>('customLabel', null),
		description: rd.useNewOrDefault<string>('description', null),
		blueprintDescription: rd.useNewOrDefault<string>('blueprintDescription', null),
		categoryId: rd.useNewOrDefault<string>('categoryId', null),
		placement: rd.useNewOrDefault<string>('placement', null),
		application: rd.useNewOrDefault<string>('application', null),
		applianceModule: rd.useNewOrDefault<string>('applianceModule', null),
		cutout: rd.useNewOrDefault<Size>('cutout', null),
		size: rd.useNewOrDefault<Size>('size', null),
		distanceAboveCountertop: rd.useNewOrDefault<number>('distanceAboveCountertop', null),
		distanceFrontOfFace: rd.useNewOrDefault<number | null>('distanceFrontOfFace', null),
		distanceBackOfFace: rd.useNewOrDefault<number | null>('distanceBackOfFace', null),
		distanceBelowCountertop: rd.useNewOrDefault<number>('distanceBelowCountertop', null),
		defaultPlacement: rd.useNewOrDefault<DefaultPlacement | null>('defaultPlacement', null),
		imageCorrection: rd.useNewOrDefault<ImageCorrection>('imageCorrection', null),
		dimensions: { ...blankComponenetDimensionOptionsCfn(), ...rd.useNewOrDefault<ComponentDimensionOptions>('dimensions', null) },
		showLabel: rd.useNewOrDefault<boolean>('showLabel', null),
		showImage: rd.useNewOrDefault<boolean>('showImage', null),
		showInOverhead: rd.useNewOrDefault<boolean>('showInOverhead', true),
		showInSkuOverhead: rd.useNewOrDefault<boolean>('showInSkuOverhead', true),
		// #392/#458: this normalizer never carried showFuelSku through from raw catalog data, so
		// Component's constructor (`data.showFuelSku ?? true`) always saw undefined and defaulted
		// to true -- no catalog/DB value for this field could ever reach a live Component, which is
		// why toggling it in Product Data (or the DB directly) never removed the LP/NG suffix or the
		// "Gas Hose Not Included" note from a pellet grill.
		showFuelSku: rd.useNewOrDefault<boolean>('showFuelSku', true),
		skuOverheadMode: rd.useNewOrDefault<SkuOverheadMode>('skuOverheadMode', 'sku'),
		skuColorPreset: rd.useNewOrDefault<string>('skuColorPreset', 'White'),
		rules: rd.useNewOrDefault<Rules>('rules', { action: [], validation: [] }),
		active: rd.useNewOrDefault<boolean>('active', true),
		fuel: rd.useNewOrDefault<boolean>('fuel', false),
		fuelFamily: rd.useNewOrDefault<string | null>('fuelFamily', assetData?.fuelFamily ?? null),
		fuelType: rd.useNewOrDefault<string | null>('fuelType', assetData?.fuelType ?? null),
		render: rd.useNewOrDefault<boolean>('render', true),
		svg: rd.useNewOrDefault<ComponentDynamicSvg>('svg', null),
		msrp: rd.useNewOrDefault<number>('msrp', 0),
		hsProductId: rd.useNewOrDefault<string | null>('hsProductId', assetData?.hsProductId ?? null),
		modelFile: rd.useNewOrDefault<string | null>('modelFile', assetData?.modelFile ?? null),
		imageFile: rd.useNewOrDefault<string | null>('imageFile', assetData?.imageFile ?? null),
		modelOffset: assetData?.modelOffset ?? rd.useNewOrDefault<{ x: number; y: number; z: number }>('modelOffset', { x: 0, y: 0, z: 0 }),
		modelRotation: assetData?.modelRotation ?? rd.useNewOrDefault<{ x: number; y: number; z: number }>('modelRotation', { x: 0, y: 0, z: 0 }),
		sleeveCompatible: rd.useNewOrDefault<boolean>('sleeveCompatible', false),
		sleeveDepth: rd.useNewOrDefault<number>('sleeveDepth', null),
		ruleManaged: rd.useNewOrDefault<boolean>('ruleManaged', true),
		fullHeight: rd.useNewOrDefault<boolean>('fullHeight', false),
		aiContext: rd.useNewOrDefault<string | null>('aiContext', null),
		placeholderModelSku: rd.useNewOrDefault<string | null>('placeholderModelSku', null)
	};

	return record;
};
